import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { fetchUserProfile, updateUserProfile } from '../../api/profile';
import { Button } from '../common/Button';
import LoadingSpinner from '../common/Loader';

const EditProfile = () => {
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [profileData, setProfileData] = useState(null);
  const userObj = useMemo(() => profileData, [profileData]);

  const [formData, setFormData] = useState({
    username: '',
    phoneNumber: '',
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const load = async () => {
      setError(null);
      setSuccess(null);

      try {
        const userId = user?.user_id;
        if (!userId) return;

        const res = await fetchUserProfile(userId);
        const data = res?.user ?? res;

        setProfileData(data);

        setFormData({
          username: data?.username ? String(data.username) : '',
          phoneNumber: data?.phoneNumber ? String(data.phoneNumber) : '',
        });
      } catch (e) {
        const msg = typeof e === 'string' ? e : e?.message || 'Failed to load profile';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const userId = user?.user_id;
    if (!userId) {
      setError('User not found.');
      return;
    }

    const payload = {};
    if (formData.username !== undefined && formData.username.trim() !== '') payload.username = formData.username.trim();
    if (formData.phoneNumber !== undefined && formData.phoneNumber.trim() !== '') payload.phoneNumber = formData.phoneNumber.trim();

    if (Object.keys(payload).length === 0) {
      setError('Please update at least one field (username or phone number).');
      return;
    }

    try {
      setSubmitting(true);
      const res = await updateUserProfile(userId, payload);

      setSuccess(res?.message || 'Profile updated successfully');

      setProfileData((prev) => ({
        ...(prev || {}),
        ...payload,
      }));
    } catch (e) {
      const msg =
        typeof e === 'string'
          ? e
          : e?.response?.data?.message || e?.message || 'Failed to update profile';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      <h1 className="text-2xl font-bold mb-6">Edit Profile</h1>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-1">
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter username"
              autoComplete="username"
            />
          </div>

          <div className="md:col-span-1">
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter phone number"
              autoComplete="tel"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-4">
          <div className="text-sm text-gray-500 mr-auto">
            {userObj?.id || userObj?.user_id ? `User ID: ${userObj?.id ?? userObj?.user_id}` : null}
          </div>

          <Button
            type="submit"
            disabled={submitting}
            width="w-48"
            className={submitting ? 'opacity-50 cursor-not-allowed' : ''}
          >
            {submitting ? 'Updating...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditProfile;
